import React, { useEffect, useState } from "react";
import { getSchedulerTrigger, updateSchedulerTrigger } from "../../../api/schedulerTriggerApi";
import { getNotificationErrorMessage } from "../../../api/whatsappNotificationsApi";
import { Alert, Field, btnPrimary, btnSecondary, card, inputClass } from "../scheduling/ui";

export default function TriggerSettings() {
  const [trigger, setTrigger] = useState(null);
  const [interval, setInterval] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const load = async () => {
    setLoading(true); setError(""); setSaved(false); setTrigger(null);
    try {
      const data = await getSchedulerTrigger();
      setTrigger(data); setInterval(data.interval_minutes == null ? "" : String(data.interval_minutes));
    } catch (err) {
      setError(getNotificationErrorMessage(err, "Could not load AWS trigger settings."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!window.confirm(`Change the global check interval to ${interval} minute(s) in ${trigger.environment}? This affects all bots, not just this bot.`)) return;
    setSaving(true); setError(""); setSaved(false);
    try {
      const data = await updateSchedulerTrigger({ interval_minutes: Number(interval), expected_revision: trigger.revision });
      setTrigger(data); setInterval(data.interval_minutes == null ? "" : String(data.interval_minutes)); setSaved(true);
    } catch (err) {
      setError(getNotificationErrorMessage(err, "Could not update AWS trigger settings. Reload before retrying."));
    } finally { setSaving(false); }
  };
  return <section className={`${card} space-y-4`} aria-label="Trigger Settings">
    <h3 className="text-base font-bold text-slate-900">Global scheduler trigger</h3>
    <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      Global · All bots. Controls how often the system checks for due schedules.
      A 10-minute interval can delay a due schedule until the next check. This is not a test mode.
    </p>
    <Alert>{error}</Alert>
    {loading && <p role="status">Loading AWS trigger settings...</p>}
    {!loading && trigger && <>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div><dt className="font-semibold">Environment</dt><dd>{trigger.environment}</dd></div>
        <div><dt className="font-semibold">AWS trigger status</dt><dd>{trigger.state}</dd></div>
        <div><dt className="font-semibold">Scheduler</dt><dd>{trigger.group} / {trigger.name}</dd></div>
        <div><dt className="font-semibold">Region</dt><dd>{trigger.region}</dd></div>
        <div><dt className="font-semibold">Current expression</dt><dd>{trigger.expression}</dd></div>
        <div><dt className="font-semibold">Flexible time window</dt><dd>{trigger.flexible_time_window.Mode}</dd></div>
      </dl>
      {trigger.state !== "ENABLED" && <p className="text-sm text-amber-800">The AWS trigger is disabled. Changing its interval will not enable it.</p>}
      {trigger.flexible_time_window.Mode !== "OFF" && <p className="text-sm text-amber-800">AWS may delay invocations within its flexible time window. Set it to OFF in AWS for predictable checks.</p>}
      <Field label="Check interval">
        <select className={inputClass} value={interval} disabled={saving} onChange={(e) => { setInterval(e.target.value); setSaved(false); }}>
          <option value="" disabled>Select interval</option>
          {[1, 5, 10, 15, 30].map((n) => <option key={n} value={n}>Every {n} {n === 1 ? "minute" : "minutes"}</option>)}
        </select>
      </Field>
      <button type="button" className={btnPrimary} disabled={saving || !interval || Number(interval) === trigger.interval_minutes} onClick={save}>{saving ? "Saving..." : "Save trigger settings"}</button>
      {saved && <p role="status" className="text-sm text-emerald-700">AWS trigger settings saved.</p>}
    </>}
    <button type="button" className={btnSecondary} disabled={loading || saving} onClick={load}>Reload trigger settings</button>
  </section>;
}
